import React, { useCallback, useEffect, useState } from "react";
import { TextInput } from "react-native";
import TextField, { TextFieldProps } from "./TextField";
import { normalizeDecimalSeparator, THOUSAND_SEPARATOR, toFixedIfNecessary } from "../utils";

export type QuantityFieldProps = Omit<TextFieldProps, "value" | "onChange" | "onChangeText"> & {
    value?: number;
    onChange?: (v?: number) => void;
    maxValue?: number;
    minValue?: number;
    allowNegativeValue?: boolean;
    decimalPlaces?: number;
    thousandSeparator?: string;
};

const QuantityField = ({
    value,
    maxValue,
    minValue,
    onChange,
    onFocus,
    onBlur,
    allowNegativeValue = false,
    decimalPlaces = 0,
    inputStyle,
    keyboardType = "decimal-pad",
    returnKeyType = "done",
    textAlign = "right",
    thousandSeparator = THOUSAND_SEPARATOR,
    withIosDoneButton = true,
    ref,
    ...props
}: QuantityFieldProps & { ref?: React.Ref<TextInput> }) => {
    const quantityMask = useCallback(
        (text = "") => {
            const result: (string | RegExp)[] = [];

            // Handle negative sign
            if (allowNegativeValue && text.startsWith("-")) {
                result.push("-");
            }

            // Keyboards in a French or German locale emit a comma as decimal
            // separator: normalize it so the mask always renders a point.
            const cleanedText = normalizeDecimalSeparator(text.replace(/[^0-9.,]/g, "")).replaceAll(
                "-",
                "",
            );

            const parts = cleanedText.split(".");
            const integerPart = parts[0] || "";

            // If starts with 0 and has more than 1 digit in integer part, and no decimal point, reject
            if (integerPart.length > 1 && integerPart[0] === "0" && parts.length === 1) {
                // Only allow the first "0"
                result.push(/\d/);
                return result;
            }

            // Add integer digits
            const digits = integerPart.split("");
            const digitCount = digits.length;

            for (let i = 0; i < digitCount; i++) {
                result.push(/\d/);
            }

            // Handle decimal places
            if (decimalPlaces > 0 && parts.length > 1) {
                result.push(".");
                const decimalPart = parts[1].slice(0, decimalPlaces);
                result.push(...decimalPart.split("").map(() => /\d/));
            }

            return result;
        },
        [allowNegativeValue, decimalPlaces],
    );

    const [localValue, setLocalValue] = useState(
        value !== undefined ? toFixedIfNecessary(value, decimalPlaces).toString() : "",
    );

    const onChangeText = useCallback(
        (newValue: string) => {
            let updateLocalValue = true;
            if (newValue !== "") {
                const formattedNewValue = normalizeDecimalSeparator(
                    newValue.replaceAll(thousandSeparator, ""),
                );
                if (decimalPlaces === 0) {
                    const intNumber = Number.parseInt(formattedNewValue);
                    if (
                        (minValue !== undefined && intNumber < minValue) ||
                        (maxValue !== undefined && intNumber > maxValue)
                    ) {
                        updateLocalValue = false;
                    } else if (!isNaN(intNumber)) {
                        onChange?.(intNumber);
                    }
                } else {
                    const floatNumber = Number.parseFloat(formattedNewValue);
                    if (
                        (minValue !== undefined && floatNumber < minValue) ||
                        (maxValue !== undefined && floatNumber > maxValue)
                    ) {
                        updateLocalValue = false;
                    } else if (!isNaN(floatNumber)) {
                        onChange?.(floatNumber);
                    }
                }
            } else {
                onChange?.(undefined);
            }

            if (updateLocalValue) {
                setLocalValue(newValue);
            }
        },
        [decimalPlaces, maxValue, minValue, onChange, thousandSeparator],
    );

    const formatWithThousandSeparator = useCallback(
        (text?: string) => {
            if (!text) return text;

            const cleaned = text.replaceAll(thousandSeparator, "");
            const parts = cleaned.split(".");
            const integerPart = parts[0] || "";

            // Only apply thousand separator for numbers >= 10000 (5 digits or more)
            if (integerPart.length >= 5) {
                const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
                return parts.length > 1 ? `${formatted}.${parts[1]}` : formatted;
            }

            return text;
        },
        [thousandSeparator],
    );

    const _onFocus = useCallback(() => {
        onFocus?.();
        setLocalValue(localValue?.replaceAll(thousandSeparator, "") || "");
    }, [localValue, onFocus, thousandSeparator]);

    const _onBlur = useCallback(() => {
        onBlur?.();
        setLocalValue(formatWithThousandSeparator(localValue) || "");
    }, [formatWithThousandSeparator, localValue, onBlur]);

    useEffect(() => {
        const valueAsString =
            value !== undefined ? toFixedIfNecessary(value, decimalPlaces).toString() : "";
        if (valueAsString !== localValue) {
            setLocalValue(valueAsString);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <TextField
            value={localValue}
            onChangeText={onChangeText}
            selectTextOnFocus
            mask={quantityMask}
            maskAutoComplete
            onFocus={_onFocus}
            onBlur={_onBlur}
            {...props}
            ref={ref}
            textAlign={textAlign}
            keyboardType={keyboardType}
            returnKeyType={returnKeyType}
            withIosDoneButton={withIosDoneButton}
            inputStyle={[
                {
                    textAlign, // Necessary to ensure native/web consistency
                },
                inputStyle,
            ]}
        />
    );
};

export default QuantityField;
